import type { ReactNode } from 'react'

import { cn } from './cn'
import { AlertCircle, AlertTriangle, Bell, CheckCircle2, Info, type IconComponent } from './icons'

export type AlertSeverity = 'success' | 'info' | 'warning' | 'error' | 'critical'

/**
 * `info` is the secondary (purple) palette, per the severity scale. Warning and critical icons
 * use their text tones, which stay readable on the soft fill.
 */
const severities: Record<
  AlertSeverity,
  { readonly box: string; readonly icon: string; readonly Icon: IconComponent }
> = {
  success: {
    box: 'bg-success-soft dark:border-success-main',
    icon: 'text-success-main',
    Icon: CheckCircle2,
  },
  info: {
    box: 'bg-secondary-soft dark:border-secondary-main',
    icon: 'text-secondary-main',
    Icon: Info,
  },
  warning: {
    box: 'bg-warning-soft dark:border-warning-main',
    icon: 'text-warning-text',
    Icon: Bell,
  },
  error: { box: 'bg-error-soft dark:border-error-main', icon: 'text-error-main', Icon: AlertCircle },
  critical: {
    box: 'bg-critical-soft dark:border-critical-main',
    icon: 'text-critical-text',
    Icon: AlertTriangle,
  },
}

export interface AlertProps {
  readonly severity?: AlertSeverity
  readonly title?: string
  readonly children?: ReactNode
  /**
   * Defaults to `alert` for warning, error and critical, which interrupt, and `status` otherwise;
   * `none` inside a live region that already announces it.
   */
  readonly role?: 'alert' | 'status' | 'none'
  readonly className?: string
}

/**
 * An inline message: the soft fill of its severity and neutral text in light mode; in dark
 * mode no fill and a border in the severity's tone, so the text stays readable.
 */
export function Alert({ severity = 'info', title, children, role, className }: AlertProps) {
  const { box, icon, Icon } = severities[severity]
  const defaultRole = severity === 'success' || severity === 'info' ? 'status' : 'alert'

  return (
    <div
      role={role ?? defaultRole}
      className={cn(
        'flex items-center gap-3 rounded border border-transparent px-4 py-1.5 dark:bg-transparent',
        box,
        className,
      )}
    >
      <Icon size={22} className={cn('shrink-0', icon)} />
      <div className="min-w-0 py-0.5 text-sm leading-6 text-text-primary">
        {title !== undefined && <p className="font-semibold">{title}</p>}
        {children}
      </div>
    </div>
  )
}
